import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  CalendarMonth as CalendarIcon,
  ContentCopy as CopyIcon,
  FilterList as FilterIcon,
  Pix as PixIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { format, subDays, startOfMonth, endOfMonth, subMonths, addMonths, isAfter, parseISO, isValid } from 'date-fns';
import toast from 'react-hot-toast';
import { api } from '@/services/api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectBankAccount } from '@/store/slices/bankAccountSlice';
import type { PixRecebido } from '@/types/bank';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const errMsg = (e: any, fallback: string) =>
  e?.response?.data?.error?.message || e?.error?.message || e?.message || fallback;

const isoDay = (d: Date) => format(d, 'yyyy-MM-dd');

const formatCurrency = (value: number | null | undefined) => {
  if (value === undefined || value === null || isNaN(value)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '-';
  const d = parseISO(value);
  if (isValid(d)) return format(d, 'dd/MM/yyyy HH:mm');
  const d2 = new Date(value);
  return isNaN(d2.getTime()) ? value : format(d2, 'dd/MM/yyyy HH:mm');
};

const formatDocumento = (doc: string | null | undefined) => {
  if (!doc) return '';
  const n = doc.replace(/\D/g, '');
  if (n.length === 11) return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (n.length === 14) return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return doc;
};

const parseDay = (s: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

const copy = (text: string, label = 'Copiado') => {
  navigator.clipboard.writeText(text).then(() => toast.success(label)).catch(() => toast.error('Não foi possível copiar'));
};

type Atalho = 'hoje' | 'ultimos7' | 'ultimos30' | 'esteMes' | 'mesPassado';

function periodoAtalho(a: Atalho): { inicio: string; fim: string } {
  const hoje = new Date();
  switch (a) {
    case 'hoje': return { inicio: isoDay(hoje), fim: isoDay(hoje) };
    case 'ultimos7': return { inicio: isoDay(subDays(hoje, 6)), fim: isoDay(hoje) };
    case 'ultimos30': return { inicio: isoDay(subDays(hoje, 29)), fim: isoDay(hoje) };
    case 'esteMes': return { inicio: isoDay(startOfMonth(hoje)), fim: isoDay(hoje) };
    case 'mesPassado': {
      const m = subMonths(hoje, 1);
      return { inicio: isoDay(startOfMonth(m)), fim: isoDay(endOfMonth(m)) };
    }
  }
}

interface RecebidosResponse {
  itens: PixRecebido[];
  total: number;
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export default function PixRecebidosPage() {
  const account = useAppSelector(selectBankAccount);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [itens, setItens] = useState<PixRecebido[]>([]);
  const [totalBackend, setTotalBackend] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [consultadoEm, setConsultadoEm] = useState<Date | null>(null);

  const [{ inicio, fim }, setPeriodo] = useState(() => periodoAtalho('ultimos7'));
  const [atalhoAtivo, setAtalhoAtivo] = useState<Atalho | null>('ultimos7');

  const periodoErro = useMemo(() => {
    const di = parseDay(inicio);
    const df = parseDay(fim);
    if (!di || !df) return 'Informe as duas datas.';
    if (isAfter(di, df)) return 'A data inicial deve ser anterior à final.';
    if (isAfter(df, addMonths(di, 12))) return 'Período máximo de 12 meses. Reduza o intervalo.';
    return null;
  }, [inicio, fim]);

  const fetchRecebidos = useCallback(async () => {
    if (periodoErro) {
      setError(periodoErro);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<RecebidosResponse>('/bank/pix/recebidos', {
        params: { inicio, fim },
        timeout: 90000,
      });
      if (response.success && response.data) {
        setItens(response.data.itens || []);
        setTotalBackend(typeof response.data.total === 'number' ? response.data.total : null);
        setConsultadoEm(new Date());
      } else {
        setError(response.error?.message || 'Erro ao consultar Pix recebidos');
      }
    } catch (err: any) {
      setError(errMsg(err, 'Erro ao consultar Pix recebidos'));
    } finally {
      setLoading(false);
    }
  }, [inicio, fim, periodoErro]);

  // Carrega ao abrir e quando a conta selecionada muda
  useEffect(() => {
    setItens([]);
    setTotalBackend(null);
    fetchRecebidos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id]);

  const aplicarAtalho = (a: Atalho) => {
    setPeriodo(periodoAtalho(a));
    setAtalhoAtivo(a);
  };

  const itensOrdenados = useMemo(
    () => [...itens].sort((a, b) => (new Date(b.horario).getTime() || 0) - (new Date(a.horario).getTime() || 0)),
    [itens]
  );

  const filtrados = useMemo(() => {
    if (!searchTerm.trim()) return itensOrdenados;
    const term = searchTerm.toLowerCase();
    const termDigits = term.replace(/\D/g, '');
    return itensOrdenados.filter(p =>
      (p.pagadorNome || '').toLowerCase().includes(term) ||
      (termDigits && (p.pagadorDocumento || '').replace(/\D/g, '').includes(termDigits)) ||
      (p.infoPagador || '').toLowerCase().includes(term) ||
      (p.e2eId || '').toLowerCase().includes(term) ||
      (p.txid || '').toLowerCase().includes(term) ||
      String(p.valor ?? '').includes(term)
    );
  }, [itensOrdenados, searchTerm]);

  const totais = useMemo(() => {
    const soma = (arr: PixRecebido[]) => arr.reduce((s, p) => s + (Number(p.valor) || 0), 0);
    const devolvido = (arr: PixRecebido[]) =>
      arr.reduce((s, p) => s + (p.devolucoes || []).reduce((d, x) => d + (Number(x.valor) || 0), 0), 0);
    return {
      periodo: soma(itens),
      periodoQtd: itens.length,
      devolvido: devolvido(itens),
      filtrado: soma(filtrados),
      filtradoQtd: filtrados.length,
    };
  }, [itens, filtrados]);

  const periodoLabel = `${format(parseDay(inicio) || new Date(), 'dd/MM/yyyy')} a ${format(parseDay(fim) || new Date(), 'dd/MM/yyyy')}`;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Pix Recebidos</Typography>
          <Typography variant="body2" color="text.secondary">
            Pix creditados na conta selecionada, consultados direto no banco
          </Typography>
          {account && (
            <Chip
              size="small"
              variant="outlined"
              icon={<AccountBalanceIcon sx={{ fontSize: 16 }} />}
              label={`${account.providerLabel} · ${account.label}${account.companyName ? ` — ${account.companyName}` : ''}`}
              sx={{ mt: 1 }}
            />
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {consultadoEm && (
            <Typography variant="caption" color="text.secondary">
              Consultado às {format(consultadoEm, 'HH:mm:ss')}
            </Typography>
          )}
          <Tooltip title="Atualizar">
            <span>
              <IconButton onClick={fetchRecebidos} disabled={loading}><RefreshIcon /></IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {!account && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Nenhuma conta bancária selecionada. Escolha uma conta no seletor do topo da página
          (Empresa · Banco). Sem seleção, o sistema usa a conta padrão da empresa.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Período */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Período
        </Typography>
        <Grid container spacing={2} sx={{ mt: 0.5 }} alignItems="center">
          <Grid item xs={6} sm={3} md={2}>
            <TextField fullWidth size="small" type="date" label="Início" value={inicio}
              InputLabelProps={{ shrink: true }} inputProps={{ max: fim || undefined }}
              onChange={(e) => { setPeriodo(p => ({ ...p, inicio: e.target.value })); setAtalhoAtivo(null); }} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField fullWidth size="small" type="date" label="Fim" value={fim}
              InputLabelProps={{ shrink: true }} inputProps={{ min: inicio || undefined, max: isoDay(new Date()) }}
              onChange={(e) => { setPeriodo(p => ({ ...p, fim: e.target.value })); setAtalhoAtivo(null); }} />
          </Grid>
          <Grid item xs={12} sm={6} md={5}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {([
                ['hoje', 'Hoje'],
                ['ultimos7', 'Últimos 7 dias'],
                ['ultimos30', 'Últimos 30 dias'],
                ['esteMes', 'Este mês'],
                ['mesPassado', 'Mês passado'],
              ] as [Atalho, string][]).map(([k, label]) => (
                <Chip key={k} label={label} size="small" clickable
                  color={atalhoAtivo === k ? 'primary' : 'default'}
                  variant={atalhoAtivo === k ? 'filled' : 'outlined'}
                  onClick={() => aplicarAtalho(k)} />
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button fullWidth variant="contained" onClick={fetchRecebidos} disabled={loading || !!periodoErro}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FilterIcon />}
              sx={{ height: '40px' }}>
              {loading ? 'Consultando...' : 'Consultar'}
            </Button>
          </Grid>
          {periodoErro && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ py: 0 }}>{periodoErro}</Alert>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Totalizador */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card variant="outlined"><CardContent sx={{ py: 2 }}>
            <Typography variant="caption" color="text.secondary">Total recebido no período</Typography>
            <Typography variant="h6" fontWeight={600} color="success.main">{formatCurrency(totais.periodo)}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card variant="outlined"><CardContent sx={{ py: 2 }}>
            <Typography variant="caption" color="text.secondary">Quantidade</Typography>
            <Typography variant="h6" fontWeight={600}>
              {totais.periodoQtd}
              {totalBackend !== null && totalBackend !== totais.periodoQtd && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  (de {totalBackend})
                </Typography>
              )}
            </Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card variant="outlined"><CardContent sx={{ py: 2 }}>
            <Typography variant="caption" color="text.secondary">Ticket médio</Typography>
            <Typography variant="h6" fontWeight={600}>
              {totais.periodoQtd ? formatCurrency(totais.periodo / totais.periodoQtd) : '—'}
            </Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card variant="outlined"><CardContent sx={{ py: 2 }}>
            <Typography variant="caption" color="text.secondary">Devolvido</Typography>
            <Typography variant="h6" fontWeight={600} color={totais.devolvido > 0 ? 'error.main' : 'text.primary'}>
              {formatCurrency(totais.devolvido)}
            </Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* Tabela */}
      <Paper variant="outlined">
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            <PixIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Recebimentos — {periodoLabel}
          </Typography>
          <TextField size="small" placeholder="Buscar pagador, documento, e2e, txid..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ width: 320 }}
          />
        </Box>
        <TableContainer sx={{ maxHeight: 640 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Horário</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell>Pagador</TableCell>
                <TableCell>Mensagem</TableCell>
                <TableCell>E2E</TableCell>
                <TableCell>txid</TableCell>
                <TableCell>Devoluções</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
              ) : !filtrados.length ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {itens.length ? 'Nenhum Pix corresponde à busca' : 'Nenhum Pix recebido no período'}
                  </Typography>
                </TableCell></TableRow>
              ) : (
                filtrados.map((p, i) => {
                  const devolvido = (p.devolucoes || []).reduce((s, d) => s + (Number(d.valor) || 0), 0);
                  return (
                    <TableRow key={p.e2eId || i} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">{formatDateTime(p.horario)}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          + {formatCurrency(Number(p.valor))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{p.pagadorNome || '—'}</Typography>
                        {p.pagadorDocumento && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {formatDocumento(p.pagadorDocumento)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color={p.infoPagador ? 'text.primary' : 'text.disabled'}
                          sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={p.infoPagador || undefined}>
                          {p.infoPagador || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{p.e2eId}</Typography>
                          {p.e2eId && (
                            <Tooltip title="Copiar E2E">
                              <IconButton size="small" onClick={() => copy(p.e2eId, 'E2E copiado')}>
                                <CopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 11 }}
                          color={p.txid ? 'text.primary' : 'text.disabled'}>
                          {p.txid || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {p.devolucoes?.length ? (
                          <Stack spacing={0.5}>
                            {p.devolucoes.map(d => (
                              <Chip key={d.id} size="small" variant="outlined" color="warning"
                                label={`${d.status} · ${formatCurrency(Number(d.valor))}`} />
                            ))}
                            {devolvido > 0 && p.devolucoes.length > 1 && (
                              <Typography variant="caption" color="text.secondary">Total {formatCurrency(devolvido)}</Typography>
                            )}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {filtrados.length > 0 && (
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {filtrados.length} de {itens.length} recebimentos
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              Total exibido: <Typography component="span" color="success.main" fontWeight={700}>{formatCurrency(totais.filtrado)}</Typography>
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
